/**
 * track:log-jump — client side of GET /api/messages/window and
 * /api/messages/by-tag, plus the sequence-cursor page used by "Load earlier".
 *
 * Every function either returns real data or throws; a failed fetch is never
 * turned into an empty result (the old BigQuery tag path cached failures as
 * empty for the whole session).
 */
import { collection, getDocs, limit, orderBy, query, where } from "firebase/firestore";
import { db } from "./firebase";
import { getAuthHeader } from "./auth";
import type { TeamLogMessage } from "../types";

export const JUMP_RADIUS = 10;
export const TAG_PAGE_SIZE = 50;

export type WindowResult =
  | { kind: "found"; messages: TeamLogMessage[] }
  | { kind: "missing"; first: number | null; last: number | null };

export async function fetchMessageWindow(seq: number, radius = JUMP_RADIUS): Promise<WindowResult> {
  const headers = await getAuthHeader();
  const res = await fetch(`/api/messages/window?seq=${seq}&radius=${radius}`, { headers });
  if (res.status === 404) {
    const body = await res.json().catch(() => ({}));
    return { kind: "missing", first: body.first ?? null, last: body.last ?? null };
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const body = await res.json();
  return { kind: "found", messages: body.messages as TeamLogMessage[] };
}

export interface TagPage {
  messages: TeamLogMessage[];
  hasMore: boolean;
}

export async function fetchTagPage(tag: string, beforeSeq?: number, pageSize = TAG_PAGE_SIZE): Promise<TagPage> {
  const headers = await getAuthHeader();
  const params = new URLSearchParams({ tag, limit: String(pageSize) });
  if (beforeSeq !== undefined) params.set("beforeSeq", String(beforeSeq));
  const res = await fetch(`/api/messages/by-tag?${params.toString()}`, { headers });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const body = await res.json();
  return { messages: (body.messages ?? []) as TeamLogMessage[], hasMore: !!body.hasMore };
}

/**
 * The server tag a sidebar filter maps to. A project filter is the
 * `project:`-prefixed convention; any other filter is the exact tag. The
 * server query is exact-match, so only one filter is sent — the stream's own
 * AND filter narrows the rest.
 */
export function serverTagForFilters(activeFilters: string[], projectNames: string[]): string | null {
  if (activeFilters.length === 0) return null;
  const projects = new Set(projectNames);
  const plain = activeFilters.find((f) => !projects.has(f));
  if (plain) return plain;
  return `project:${activeFilters[0]}`;
}

/**
 * One page of posts older than `oldestSequence`, newest first. Pages by
 * `sequence` (monotonic, gapless, always a number), never `timestamp`, which
 * mixes Firestore Timestamp and ISO-string values and sorts the two types
 * apart — a timestamp cursor skips or repeats posts across that boundary
 * (see getMessages in server/modules/team/service.ts).
 */
export async function fetchEarlierPage(
  workspaceId: string,
  oldestSequence: number,
  pageSize: number,
): Promise<TeamLogMessage[]> {
  const q = query(
    collection(db, "team_log_messages"),
    where("workspace_id", "==", workspaceId),
    where("sequence", "<", oldestSequence),
    orderBy("sequence", "desc"),
    limit(pageSize),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<TeamLogMessage, "id">) }));
}
